import { useAsyncCheckoutEnabled } from "../../../hooks/useAsyncCheckoutEnabled";
import { CartTotal } from "../CartTotal";
import { SectionTitle } from "../CollapsibleSection";
import DebugPanel from "../DebugPanel";
import { RetailerError } from "../BuyBoxError";
import { OrderSummaryProduct } from "./OrderSummaryProduct";
import { Box, Skeleton, styled, useMediaQuery, useTheme } from "@mui/material";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";
import {
  formatMoney,
  FourPointStarIcon,
  getEntityImage,
  getFeaturedRetailerImage,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";

import { SubmitCheckoutButton } from "../SubmitCheckoutButton";
import { BuyBoxRetailerTerms } from "../BuyBoxRetailerTerms";
import { useCheckoutUI } from "../../../CheckoutUIProvider";

const Container = styled("div")({
  boxSizing: "border-box",
  display: "flex",
  borderRadius: "8px",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
  backgroundColor: "#f6f8fa",
  maxWidth: 475,
  minWidth: 400,
  padding: "32px 32px 16px 32px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "16px",
    backgroundColor: "white",
    padding: "10px 0px 10px 0px",
    maxWidth: "100%",
    minWidth: "100%",
  },
});

const CartContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
});

const CashBackSwatch = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  borderRadius: 24,
  marginTop: "16px",
  gap: 6,
  width: "fit-content",
  fontWeight: 600,
  fontSize: 14,
  color: theme.vars.palette.text.primary,
  boxShadow: "none",
}));

const FloatingOrderSummaryContainer = styled("div")({
  width: "100vw",
  boxSizing: "border-box",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  position: "fixed",
  bottom: 0,
  left: 0,
  padding: "16px",
  backgroundColor: "white",
  boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  display: "none",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "flex",
  },
});

const RetailerLogo = styled("img")(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  objectFit: "cover",
  marginRight: "4px",
  [theme.breakpoints.down("md")]: {
    width: "18px",
    height: "18px",
    marginRight: "2px",
  },
}));

const OrderSummary = ({
  showOrderSummaryTitle = true,
}: {
  showOrderSummaryTitle?: boolean;
}) => {
  const [state] = useCheckoutUI();
  const showSubmitButton =
    state.value === "ready_to_checkout" || state.value === "checking_out";

  const isMobile = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const { debug } = useAsyncCheckoutEnabled();
  const { items, isLoading, rewardAmounts } = useCheckoutCartInfo();

  const currentRetailerId = items[0]?.retailerId;

  if (isLoading) {
    return (
      <CartContainer>
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
      </CartContainer>
    );
  }

  if (!items?.length) {
    return null;
  }

  return (
    <Container>
      {showOrderSummaryTitle && (
        <div
          style={{
            width: "100%",
            marginBottom: "19.7px",
          }}
        >
          <SectionTitle>
            <div className="collapsible-section-title">
              {currentRetailerId ? (
                <RetailerLogo
                  className="retailer-logo"
                  alt="retailer logo"
                  src={getFeaturedRetailerImage(currentRetailerId)}
                  onError={(e) => {
                    e.currentTarget.src = getEntityImage(
                      currentRetailerId,
                      "retailer"
                    );
                  }}
                ></RetailerLogo>
              ) : null}
              Order summary
            </div>
          </SectionTitle>
        </div>
      )}
      <CartContainer>
        {items.map((item) => (
          // @ts-ignore
          <OrderSummaryProduct key={item.stacklineSku} product={item} />
        ))}
      </CartContainer>

      {debug && <DebugPanel />}
      <RetailerError />
      <div
        style={{
          width: "100%",
          marginTop: 12.4,
          marginBottom: 16,
          paddingBottom: isMobile ? 15 : 0,
        }}
      >
        <CartTotal />

        {showSubmitButton && isMobile ? null : (
          <div style={{ paddingTop: 16 }}>
            <SubmitCheckoutButton text="Place my order" />
            {rewardAmounts.totalRewardAmount > 0 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="16px"
                width="100%"
              >
                <CashBackSwatch>
                  <FourPointStarIcon sx={{ width: 13.5, height: 13.5 }} />
                  You'll earn {formatMoney(rewardAmounts.totalRewardAmount)} in
                  cash rewards
                </CashBackSwatch>
              </Box>
            )}
            <BuyBoxRetailerTerms sx={{ marginTop: "16px" }} />
          </div>
        )}
      </div>
    </Container>
  );
};

export const FloatingOrderSummary = () => {
  const [state] = useCheckoutUI();
  const showSubmitButton =
    state.value === "ready_to_checkout" || state.value === "checking_out";
  if (!showSubmitButton) {
    return null;
  }
  return (
    <FloatingOrderSummaryContainer>
      <SubmitCheckoutButton text="Place my order" />
      <BuyBoxRetailerTerms />
    </FloatingOrderSummaryContainer>
  );
};

export default OrderSummary;
